import type { ProjectRow } from "@/lib/profitability-api";

export interface ProjectTotal {
  entityId: string;
  projectName: string;
  revenue: number;
  /** Null when the rows carry no cost or hours (Aggregate users). */
  cost: number | null;
  hours: number | null;
  marginPct: number | null;
  /** How many periods were summed -- the drill-in shows them. */
  periods: number;
  budgetHours: number | null;
  variancePct: number | null;
  hasBudget: boolean;
  billingType: string | null;
}

/**
 * One row per project instead of one per project per month.
 *
 * The account page listed a 12-month SOW as twelve near-identical rows, so a
 * five-project account filled three screens and no row was the project.
 *
 * Margin is recomputed from the summed revenue and cost -- NOT averaged from
 * the monthly margins. Averaging percentages weights a $200 month the same as
 * a $200,000 one.
 *
 * Aggregate users get no cost, so there the margin is the REVENUE-WEIGHTED
 * mean of the monthly margins: sum(revenue x margin) / sum(revenue). That is
 * the same number, algebraically -- revenue x margin is revenue minus cost --
 * so long as every month has a margin. A month with no margin (no revenue) may
 * still carry cost the weighting cannot see, so then the margin is null:
 * unknown, rather than a figure that quietly leaves that cost out.
 */
export function rollUpProjects(rows: ProjectRow[]): ProjectTotal[] {
  const totals = new Map<
    string,
    ProjectTotal & { _periods: Set<string>; _weighted: number; _unknownMargin: boolean }
  >();

  for (const row of rows) {
    const key = row.entity_id.toUpperCase();
    const bucket = totals.get(key) ?? {
      entityId: row.entity_id,
      projectName: row.project_name ?? row.entity_id,
      revenue: 0,
      cost: 0 as number | null,
      hours: 0 as number | null,
      marginPct: null,
      periods: 0,
      // Budget is a project-level figure repeated on every period row, so it is
      // taken, not summed: adding it up multiplies it by the month count.
      budgetHours: row.budget_hours ?? null,
      variancePct: row.variance_pct,
      hasBudget: row.has_budget,
      billingType: row.billing_type,
      _periods: new Set<string>(),
      _weighted: 0,
      _unknownMargin: false,
    };
    bucket.revenue += row.revenue;
    bucket.cost = bucket.cost === null || row.cost === undefined ? null : bucket.cost + row.cost;
    bucket.hours =
      bucket.hours === null || row.hours === undefined ? null : bucket.hours + row.hours;
    if (row.margin_pct === null) bucket._unknownMargin = true;
    else bucket._weighted += row.revenue * row.margin_pct;
    bucket._periods.add(row.period_id);
    totals.set(key, bucket);
  }

  return [...totals.values()]
    .map(({ _periods, _weighted, _unknownMargin, ...t }) => ({
      ...t,
      periods: _periods.size,
      marginPct:
        t.revenue <= 0
          ? null
          : t.cost !== null
            ? ((t.revenue - t.cost) / t.revenue) * 100
            : _unknownMargin
              ? null
              : _weighted / t.revenue,
    }))
    .sort((a, b) => b.revenue - a.revenue);
}
